import { normalizeError } from './redaction'
import type { NormalizedError } from './types'

type ErrorSummary = Omit<NormalizedError, 'causes'>

function read(value: unknown, key: PropertyKey): unknown {
  try {
    return value && (typeof value === 'object' || typeof value === 'function')
      ? Reflect.get(value, key)
      : undefined
  } catch {
    return undefined
  }
}

/**
 * The SDK's cause fields cannot be changed by beforeSend. Give it a bounded
 * diagnostic copy; business errors and their retry metadata remain untouched.
 */
export function createRumErrorDiagnostic(original: unknown): {
  error: Error
  aggregation: Array<ErrorSummary>
} {
  const aggregation: Array<ErrorSummary> = []
  const seen = new WeakSet<object>()
  // One primary error plus at most three causes/aggregate members combined.
  let remaining = 4
  const copy = (value: unknown): Error => {
    remaining -= 1
    const isObject =
      value !== null &&
      (typeof value === 'object' || typeof value === 'function')
    let details: NormalizedError
    if (isObject && seen.has(value)) {
      details = { kind: 'Error', message: '[Circular error cause]' }
    } else {
      if (isObject) {
        seen.add(value)
      }
      let isError = false
      try {
        isError = value instanceof Error
      } catch {
        // Revoked proxies are not useful diagnostic inputs.
      }
      const isErrorLike =
        typeof read(value, 'name') === 'string' &&
        typeof read(value, 'message') === 'string'
      if (
        typeof value === 'function' ||
        (isObject && !isError && !isErrorLike)
      ) {
        // Never traverse/stringify arbitrary payloads or callable source.
        details = { kind: 'Error', message: 'Non-Error exception reported' }
      } else {
        // Starting at depth two normalizes primary fields and any registered
        // safe override, but never traverses nested cause/aggregate details.
        details = normalizeError(value, 2)
      }
    }
    const diagnostic = new Error(details.message)
    diagnostic.name = details.kind
    diagnostic.stack = details.stack
    if (details.code) {
      Object.assign(diagnostic, { code: details.code })
    }
    if (!details.causes?.length || remaining === 0) {
      return diagnostic
    }
    const members = read(value, 'errors')
    let isAggregate = false
    try {
      isAggregate = Array.isArray(members)
    } catch {
      // An unavailable optional aggregate must not erase the primary error.
    }
    if (isAggregate) {
      const length = read(members, 'length')
      const count = typeof length === 'number' ? Math.min(length, 3) : 0
      for (let index = 0; index < count && remaining > 0; index++) {
        const member = copy(read(members, index))
        const code = read(member, 'code')
        aggregation.push({
          kind: member.name,
          message: member.message,
          ...(member.stack ? { stack: member.stack } : {}),
          ...(typeof code === 'string' ? { code } : {}),
        })
      }
    } else {
      const cause = read(value, 'cause')
      if (cause !== undefined) {
        diagnostic.cause = copy(cause)
      }
    }
    return diagnostic
  }
  return { error: copy(original), aggregation }
}
